import { OutlinedInput, InputAdornment, Stack, Button } from "@mui/material";
import ListPageHeader, { ListPageHeaderLeft, ListPageHeaderRight } from "../../../../Components/Misc/ListPageHeader";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import { useState, useMemo, useEffect, useContext, useCallback } from "react";
import { DbStorageInfos } from "../../../../Core/Api/Types/db-storage-infos";
import TableSkeleton from "../../../../Components/Table/TableSkeleton";
import DataTable, { CellSize } from "../../../../Components/Table/table";
import { GridActionsCellItem, GridColDef, GridRowParams, GridRowsProp } from "@mui/x-data-grid";
import { setupService } from "../../../../Core/Api/services";
import { ModalContext } from "../../../../Components/Modals";
import ModalStorageEdit from "../../../../Components/Modals/ModalStorageEdit/modal-storage-edit";
import TableCellChip from "../../../../Components/Table/TableCellChip/table-cell-chip";
import TableCellBool from "../../../../Components/Table/TableCellBool";
import { useTranslation } from "react-i18next";
import TableUuids from "../../../../Components/Table/table-uuids";

function AdminSettingsStorages() {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(true);
    const modalContext = useContext(ModalContext);

    const { openModal } = { ...modalContext };

    const [filterText, setFilterText] = useState("");
    const [rows, setRows] = useState<DbStorageInfos[]>([]);

    useEffect(() => {
        setupService.GetAllStorages().then((data) => {
            setRows(data.storages ?? []);
            setIsLoading(false);
        });
    }, []);

    // #region Grid definition
    let _rows: GridRowsProp = [];
    type Row = (typeof _rows)[number];

    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: "id",
                headerName: "Id",
                flex: CellSize.xxsmall,
                minWidth: CellSize.xxsmall,
            },
            {
                field: "platformIds",
                headerName: t("common:platform"),
                flex: CellSize.xxsmall,
                minWidth: CellSize.xxsmall,
            },
            {
                field: "name",
                headerName: t("common:name"),
                flex: CellSize.xlarge,
                minWidth: CellSize.xlarge,
            },
            {
                field: "enabled",
                headerName: t("common:enabled"),
                flex: CellSize.xsmall,
                minWidth: CellSize.xsmall,
                renderCell: (data: Row) => <TableCellBool value={data.value} />,
            },
            {
                field: "allowDelete",
                headerName: t("table:headerName.deleteAllowed"),
                flex: CellSize.xsmall,
                minWidth: CellSize.xsmall,
                renderCell: (data: Row) => <TableCellBool value={data.value} />,
            },
            {
                field: "type",
                headerName: t("common:type"),
                flex: CellSize.small,
                minWidth: CellSize.small,
            },
            {
                field: "context",
                headerName: t("common:context"),
                flex: CellSize.small,
                minWidth: CellSize.small,
            },
            {
                field: "kind",
                headerName: t("common:kind"),
                flex: CellSize.small,
                minWidth: CellSize.small,
            },
            {
                resizable: true,
                field: "objectTypes",
                headerName: t("common:objectTypesUpperCase"),
                flex: CellSize.large,
                minWidth: CellSize.large,
                renderCell: (data: Row) => <TableCellChip data={data} />,
            },
            {
                headerName: "",
                field: "actions",
                type: "actions",
                align: "right",
                getActions: (params: GridRowParams) => {
                    if (!params || !params.row) return;
                    return [
                        <GridActionsCellItem
                            icon={<ContentCopyOutlinedIcon />}
                            label={t("common:copy")}
                            onClick={() => openStorageEditModal(true, { ...params.row, id: -500 })}
                            showInMenu
                        />,
                        <GridActionsCellItem
                            icon={<ModeEditOutlineOutlinedIcon />}
                            label={t("common:editUpperCase")}
                            onClick={() => openStorageEditModal(false, params.row)}
                            showInMenu
                        />,
                        <GridActionsCellItem
                            icon={<TableCellBool value={params?.row?.enabled} />}
                            label={`${t("common:enable")} / ${t("common:disable")}`}
                            onClick={() => {
                                setupService
                                    .UpdateStorage({ ...params.row, enabled: !params?.row?.enabled })
                                    .then((data) =>
                                        data.success
                                            ? setRows([
                                                ...(rows.filter((x) => x.id != params?.row?.id) ?? []),
                                                { ...params.row, enabled: !params?.row?.enabled },
                                            ])
                                            : null
                                    );
                            }}
                            showInMenu
                        />,
                    ];
                },
            },
        ],
        [rows]
    );

    const openStorageEditModal = useCallback(
        (isCreation: boolean, storage: DbStorageInfos) => {
            let feedbackData = isCreation
                ? (newStorage) => setRows([...rows, newStorage])
                : (UpdatedStorage) => setRows([...rows.filter((x) => x.id != storage.id), UpdatedStorage]);
            if (!openModal) return;
            openModal(ModalStorageEdit, { isCreation, storage, listOfAllStorages: rows, feedbackData });
        },
        [openModal, rows]
    );

    const additionalActions = (
        <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => {
                openStorageEditModal(true, new DbStorageInfos());
            }}
        >
            {t("button:storage")}
        </Button>
    );

    return (
        <DataTable
            loading={isLoading}
            tableKey={TableUuids.SETTINGS_STORAGES}
            columns={columns}
            rows={rows}
            additionalActions={additionalActions}
            rowHeight={64}
            pagination={true}
            disableRowSelectionOnClick
            // apiRef={apiGrid}
            getRowHeight={() => "auto"}
        />
    );
}

export default AdminSettingsStorages;
